import {Tooltip} from '@mui/material'
import {Box, Loading, Skeleton, Typography} from '@viptech/react-components'
import {useEffect, useRef, useState} from 'react'
import ReactApexChart from 'react-apexcharts'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'

type LineChartAccessPerWeekProps = {
  isLoading?: boolean
  labelsXaxis?: Array<string>
  data: ApexAxisChartSeries | ApexNonAxisChartSeries
  title: string
  isRefetching?: boolean
}
const GAP = 52

const MOCK_SIZES = [
  60, 44, 47, 89, 146, 26, 142, 86, 83, 135, 115, 28, 149, 52, 43, 93, 80, 48, 100, 37, 144, 145, 109, 71, 83, 102, 26, 135, 150, 116, 70, 103, 149, 49, 133,
  78, 85, 64, 29, 85, 110, 132, 31, 101, 134, 44, 122, 125, 123, 45,
]

function DashboardStackChart({isLoading, isRefetching, data, labelsXaxis, title}: LineChartAccessPerWeekProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const {styledTheme} = usePersonalizedThemeByCompany()

  const [containerWidth, setContainerWidth] = useState(0)
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - GAP)
      }
    }

    updateContainerWidth()

    window.addEventListener('resize', updateContainerWidth)
    const observer = new ResizeObserver(updateContainerWidth)
    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', updateContainerWidth)
      if (containerRef.current) {
        observer.unobserve(containerRef.current)
      }
    }
  }, [])

  const opts: ApexCharts.ApexOptions = {
    series: data,
    chart: {
      type: 'area',
      stacked: true,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      fontFamily: 'Inter, sans-serif',
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      x: {
        format: 'dd/MM HH:mm',
      },
    },
    xaxis: {
      categories: labelsXaxis ?? [],
      type: 'datetime',
      labels: {
        trim: true,
        hideOverlappingLabels: true,
        rotate: 0,
        style: {
          fontSize: '12px',
        },
        datetimeUTC: false,
      },
    },
    stroke: {
      width: 1,
      curve: 'smooth',
    },
    fill: {
      type: '',
    },
    title: {
      margin: 41,
      align: 'center',
      offsetX: -5,
      offsetY: -15,
      style: {
        fontSize: '14px',
        fontWeight: 0,
        fontFamily: "'Inter', 'sans-serif",
        color: '#364152',
      },
    },
    subtitle: {
      offsetX: 0,
      offsetY: 60,
      align: 'left',
      style: {
        fontSize: '14px',
        fontWeight: 'none',
        fontFamily: "'Inter', 'sans-serif",
        color: '#8E8E8E',
      },
    },
    grid: {
      show: false,
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
    },
    noData: {
      text: 'Nenhum dado foi encontrado',
      align: 'center',
      verticalAlign: 'top',
      offsetX: 5,
      offsetY: 15,
      style: {
        color: '#8E8E8E',
        fontSize: '16px',
        fontFamily: "'Inter', 'sans-serif",
      },
    },
    states: {
      active: {
        allowMultipleDataPointsSelection: true,
      },
    },
  }
  const skeletonWidth = title === 'Registros por Objetos' ? 23 : 11
  return (
    <Box
      ref={containerRef}
      backgroundColor='white'
      borderRadius='16px'
      width='100%'
      position='relative'
      height='300px'
      style={{boxShadow: '0px 5px 8px 0px #00000026', display: 'grid'}}>
      <Box display='flex' direction='column' padding='24px 32px' rowGap='6px' align='center' flex={1}>
        <Typography variant='label' size='16px' marginTop='8px' color={styledTheme.dashboard.colors.title} weight={600}>
          {title}
        </Typography>

        {isRefetching && !isLoading && (
          <Tooltip title='Buscando atualizações' placement='top'>
            <Box position='absolute' right='24px' display='flex' columnGap='8px'>
              <Loading size='20px' />
            </Box>
          </Tooltip>
        )}

        {isLoading ? (
          <Box width={'100%'} height={'200px'} display='flex' align='flex-end' columnGap='24px' backgroundColor='white' overflow='hidden'>
            <Box>
              {new Array(10).fill(0).map((_, index) => (
                <Skeleton width='12px' height='8px' borderRadius='4px' key={index} />
              ))}
            </Box>
            {new Array(skeletonWidth).fill(0).map((_, index) => (
              <Skeleton width='48px' height={`${MOCK_SIZES[index]}px`} borderRadius='4px' key={index} />
            ))}
          </Box>
        ) : (
          <ReactApexChart options={opts} series={opts.series} type='area' height={220} width={containerWidth} />
        )}
      </Box>
    </Box>
  )
}

export default DashboardStackChart
